import { useCallback, useEffect, useState } from "react";
import { send } from "../api/auth";
import { console_, type Member, type RoleOption } from "../api/console";
import { AddButton } from "../design/AddLink";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { toneOf, type Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";

// ---------------------------------------------------------------------------
// The people inside the agency.
//
// THIS SCREEN SHOWS NO EMAIL ADDRESSES, AND THAT IS NOT AN OVERSIGHT.
// app.org_member carries a user_account_id and nothing else about the person;
// app.user_account lives behind a different Postgres role that the tenant
// connection holds no privilege on at all (V3's central decision). The roster
// of who works where, complete with addresses, is exactly the object a tenant
// compromise wants, and it is not assembled here.
//
// The consequence is honest rather than hidden: the table shows the account
// reference and the role, an invitation is addressed by typing an address, and
// the person receiving it is the only one who can complete it.
//
// AN INVITED MEMBER IS NOT A MEMBER. app.actor_role() only recognises `active`,
// so an invited row grants nothing at all — and the activation guard in
// migration 0007 means nobody here can switch it on. The chip says "Invited"
// in amber for that reason: it is a state that needs somebody to act, not a
// state that is working.
// ---------------------------------------------------------------------------

// A failed read keeps its status, so a limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T };

export function People() {
  const { apiBase, me } = useSession();
  const [loaded, setLoaded] = useState<Loaded<Member[]>>({ state: "loading" });
  // TWO LISTS, AND THE REASON IS A DEFECT THAT ONLY SHOWED ON SCREEN.
  //
  // The first version kept one list, filtered to internal roles for the invite
  // select, and used the same list to LABEL the table. A client user is a member
  // with an external role, so the lookup missed and the table rendered the raw
  // id — `client_viewer` — at a person who has never seen a snake_case
  // identifier in their life. Every test passed; it took one page load.
  //
  // `allRoles` labels; `invitable` is offered. They are different questions.
  const { t, refusal, locale } = useT();
  const [inviting, setInviting] = useState(false);
  const [allRoles, setAllRoles] = useState<RoleOption[]>([]);
  const [invitable, setInvitable] = useState<RoleOption[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: Tone; status: string; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const { status, body } = await send<{ members?: Member[]; error?: { message?: string } }>(
        console_.listMembers(apiBase),
      );
      if (status !== 200 || !body.members) {
        setLoaded({ state: "error", failure: { status, refusal: refusal(body.error) } });
        return;
      }
      setLoaded({ state: "ready", data: body.members });
    } catch {
      setLoaded({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk.") } });
    }
  }, [apiBase, t, refusal]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { status, body } = await send<{ org_roles?: RoleOption[] }>(console_.roles(apiBase));
        if (!live || status !== 200 || !body.org_roles) return;
        setAllRoles(body.org_roles);
        // EXTERNAL ROLES ARE NOT OFFERED, and the filter reads the database's
        // own `is_external` rather than a list of names. A client viewer is
        // invited from the client's own screen, where a client account is in
        // hand to attach them to — inviting one from here would create an
        // external member entitled to nothing at all. Nor a platform role (it belongs
        // to no organisation, and the database refuses it) or the robot's (0269).
        setInvitable(body.org_roles.filter((r) => !r.is_external && !r.is_platform && !r.is_robot));
      } catch {
        /* leaves the select empty, which is visible, rather than guessing a role */
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);

  const mayManage = me?.authenticated === true && me.permissions.includes("member.manage");

  async function act(spec: Parameters<typeof send>[0], success: string) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { status, body } = await send<{ error?: { message?: string } }>(spec);
      if (status >= 400) {
        setNotice({ tone: "failed", status: t("Not saved"), message: refusal(body.error) ?? t("That did not work.") });
        return;
      }
      setNotice({ tone: "live", status: t("Saved"), message: success });
      await load();
    } catch {
      setNotice({ tone: "attention", status: t("No connection"), message: t("We could not reach Verbunk.") });
    } finally {
      setBusy(false);
    }
  }

  // A quiet list: a status chip only for the people who are not simply active, and no column at all when everybody is.
  const anyOff = loaded.state === "ready" && loaded.data.some((m) => toneOf(m.status_tone) !== "live");

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-head">
          <h2>{t("People")}</h2>
          {mayManage ? <AddButton label={t("Invite a colleague")} expanded={inviting} onClick={() => setInviting(!inviting)} /> : null}
        </div>

        {mayManage && inviting ? (
          <>
          <p className="muted">
            {t("They will get an email with a link. Their access starts when they open it — until then the invitation grants nothing.")}
          </p>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (inviteRole === "") return;
              void act(
                console_.inviteMember(apiBase, inviteEmail, inviteRole, inviteName),
                t("The invitation is on its way."),
              ).then(() => {
                setInviteEmail("");
                setInviteName("");
                setInviting(false);
              });
            }}
          >
            <div className="form-grid">
            <div className="field">
              <label htmlFor="member-invite-email">{t("Email address")}</label>
              <input
                id="member-invite-email"
                type="email"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="member-invite-name">{t("Name")}</label>
              <input id="member-invite-name" autoComplete="off" maxLength={120} value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
              <p className="field-hint">{t("Optional. The person can change it in their own account.")}</p>
            </div>
            <div className="field">
              <label htmlFor="member-invite-role">{t("Role")}</label>
              {/* THERE IS NO DEFAULT ROLE, AND THAT IS THE POINT.
                  The first version selected the first option, and app.org_role
                  is ordered by `rank`, which puts OWNER first — so the invite
                  form defaulted to handing over full control of the
                  organisation, including ownership transfer, to whoever was
                  typed in the box above. Nor is there a safe automatic
                  alternative: `rank` is a display order, not a privilege
                  ordering, so any derived default would be a guess. "What may
                  this person do" is a question with no correct default, so it
                  is asked. */}
              <select
                id="member-invite-role"
                required
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="">{t("Choose a role…")}</option>
                {invitable.map((role) => (
                  <option key={role.id} value={role.id}>
                    {translateLabel(locale, role.label)}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                {invitable.find((r) => r.id === inviteRole)?.description ??
                  t("Choose what this person will be able to do.")}
              </p>
            </div>
            </div>
            <div className="row">
              <button className="btn" type="submit" disabled={busy || inviteRole === ""}>
                {busy ? t("Working…") : t("Send invitation")}
              </button>
              <button className="btn btn-quiet" type="button" onClick={() => setInviting(false)}>
                {t("Cancel")}
              </button>
            </div>
          </form>
          </>
        ) : null}

        <div className="form-message" role="status" aria-live="polite">
          {notice ? (
            <>
              <StatusChip tone={notice.tone} label={notice.status} />
              <span>{notice.message}</span>
            </>
          ) : null}
        </div>

        {loaded.state === "loading" ? <p className="muted">{t("Loading…")}</p> : null}

        {loaded.state === "error" ? (
          <LoadFailed failure={loaded.failure} fallback={t("We could not load your people.")} onRetry={() => void load()} />
        ) : null}

        {loaded.state === "ready" ? (
          <table className="data-table">
            <caption className="visually-hidden">{t("People in this organisation")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Person")}</th>
                <th scope="col">{t("Role")}</th>
                {anyOff ? <th scope="col">{t("Status")}</th> : null}
                <th scope="col">
                  <span className="visually-hidden">{t("Actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loaded.data.map((member) => (
                <tr key={member.id}>
                  <th scope="row">
                    {member.display_name ?? member.email ?? member.user_account_id.slice(0, 8)}
                    {member.is_self ? ` ${t("(you)")}` : ""}
                    {member.display_name && member.email ? <span className="muted"><br />{member.email}</span> : null}
                  </th>
                  <td>{translateLabel(locale, allRoles.find((r) => r.id === member.role)?.label ?? member.role_label ?? member.role)}</td>
                  {anyOff ? (
                    <td>
                      {toneOf(member.status_tone) !== "live" ? (
                        <StatusChip tone={toneOf(member.status_tone)} label={translateLabel(locale, member.status_label)} />
                      ) : null}
                    </td>
                  ) : null}
                  <td>
                    {/* SUSPENDING YOURSELF IS NOT OFFERED. The server would
                        also refuse to remove the last owner, but a control that
                        signs you out of your own console is one nobody should
                        reach by accident. */}
                    {mayManage && !member.is_self && member.move ? (
                      <button
                        className="btn btn-quiet"
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          const move = member.move;
                          if (move) void act(console_.updateMember(apiBase, member.id, { status: move.status }), move.done);
                        }}
                      >
                        {translateLabel(locale, member.move.label)}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

    </div>
  );
}
